import { Box, Button, Card, Grid, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RoutePaths } from '../routes/AppRoutes';
import { PageWrapper } from '../common/components/PageWrapper';
import { useProductVersionByIdQuery } from '../queries/__generated__/productVersionById.generated';
import AddPackagingsToSubCatalogsDialog from '../company/catalogs/subCatalogs/AddPackagingsToSubCatalogsDialog';
import useOpenable from '../hooks/useOpenable';
import TopAppBar from '../common/components/TopAppBar';
import { canEditProductAtom } from '../company/state';
import ProductInfo from './productPage/Product/ProductInfo';
import BaseUnit from './productPage/Product/BaseUnit/BaseUnit';

export default function ProductPage(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { open, isOpen, close } = useOpenable();
  const { t } = useTranslation(['common', 'products']);
  const theme = useTheme();
  const { productSku, companyId } = useParams();
  const { data: productData } = useProductVersionByIdQuery({
    variables: {
      companyId,
      productSku: Number(productSku),
      shouldCheckHistory: false,
    },
  });
  const [newInsertedPackagingsSkus, setNewInsertedPackagingsSkus] = useState<number[]>([]);
  const canEdit = useAtomValue(canEditProductAtom);

  useEffect(() => {
    // make sure to reset navigation state so that the dialog only opens if coming directly from a submit of product form
    navigate(location.pathname, { replace: true });
    if (location.state?.newAddedPackagingsSkus?.length) {
      setNewInsertedPackagingsSkus(location.state.newAddedPackagingsSkus);
      open();
    }
  }, []);

  const product = useMemo(() => {
    return productData?.product?.[0];
  }, [productData]);

  const baseUnitsGridContainerLgColumn = useMemo((): number => {
    return product?.base_units!.length === 1 ? 4 : 8;
  }, [product]);

  const baseUnitsGridItemLgColumn = useMemo((): number => {
    return product?.base_units!.length === 1 ? 12 : 6;
  }, [product]);

  return (
    <>
      <TopAppBar
        configuration="small"
        backTo={RoutePaths.COMPANY_PRODUCTS}
        shouldCheckHistory={false}
        title={t('products:product_details')}
      />
      <AddPackagingsToSubCatalogsDialog
        companyId={companyId as string}
        newPackagingsSkus={newInsertedPackagingsSkus}
        close={close}
        open={isOpen}
      />
      <PageWrapper
        sx={{
          pt: {
            xs: 0.5,
            lg: 2,
          },
        }}
      >
        {product && (
          <Grid container columnSpacing={2} rowSpacing={2} sx={{ justifyContent: 'center' }}>
            <Grid
              size={{ xs: 12, md: 7, lg: 4 }}
              sx={{
                [theme.breakpoints.up('md')]: {
                  position: 'sticky',
                  top: 80,
                  alignSelf: 'start',
                },
              }}
            >
              <Card sx={{ p: 2, borderRadius: 4 }}>
                <ProductInfo
                  product={product}
                  isNameToOptimize={product.product_naming_validation?.is_valid === false && !product.product_naming_validation.pending_validation}
                />
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 5, lg: baseUnitsGridContainerLgColumn }}>
              <Box
                sx={{
                  p: {
                    xs: 2,
                    md: 3,
                  },
                }}
              >
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="titleMedium">{t('products:base_unit', { count: product.base_units.length })}</Typography>
                    {canEdit && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        component={Link}
                        to={`${RoutePaths.PRODUCT_ADD_PACKAGING}?productId=${product.id}`}
                      >
                        {t('common:add')}
                      </Button>
                    )}
                  </Grid>
                  {product.base_units.map((baseUnit, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 12, lg: baseUnitsGridItemLgColumn }} key={baseUnit.id}>
                      <BaseUnit
                        baseUnit={baseUnit}
                        baseUnitIndex={index}
                        product={product}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        )}
      </PageWrapper>
    </>
  );
}
